'use client'

import {
  applyMockReceivingPoll,
  tripNeedsReceivingPoll,
  type StoreCompletedEvent,
  type StoreScannedEvent,
} from '@/features/delman/receivingPollSync'
import { fetchTripDetailFromApi } from '@/features/delman/api/delmanTripApi'
import { shouldUseDelmanMock } from '@/features/delman/api/delmanApiMode'
import type { TripRecord } from '@/features/delman/storage/tripStorage'
import { useEffect, useRef } from 'react'

const POLL_INTERVAL_MS = 3_000

type UseReceivingPollOptions = {
  enabled: boolean
  getTrip: () => TripRecord | null
  onTripUpdate: (trip: TripRecord) => void
  onStoreScanned?: (event: StoreScannedEvent) => void
  onStoreCompleted?: (event: StoreCompletedEvent) => void
}

function diffReceivingEvents(
  prev: TripRecord,
  next: TripRecord,
): { scanned: StoreScannedEvent[]; completed: StoreCompletedEvent[] } {
  const scanned: StoreScannedEvent[] = []
  const completed: StoreCompletedEvent[] = []

  for (const stop of next.stops) {
    const before = prev.stops.find((s) => s.id === stop.id)
    if (!before) continue

    if (before.receivingStatus === 'none' && stop.receivingStatus === 'draft') {
      scanned.push({ deliveryId: stop.id, storeName: stop.storeName })
    }
    if (before.receivingStatus !== 'completed' && stop.receivingStatus === 'completed') {
      completed.push({ deliveryId: stop.id, storeName: stop.storeName })
    }
  }

  return { scanned, completed }
}

/** Pantau perubahan status receiving (mock polling atau refresh API). */
export function useReceivingPoll({
  enabled,
  getTrip,
  onTripUpdate,
  onStoreScanned,
  onStoreCompleted,
}: UseReceivingPollOptions) {
  const scannedRef = useRef<Set<string>>(new Set())
  const completedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!enabled) return undefined

    function emitEvents(
      newlyScanned: StoreScannedEvent[],
      newlyCompleted: StoreCompletedEvent[],
    ) {
      for (const event of newlyScanned) {
        if (scannedRef.current.has(event.deliveryId)) continue
        scannedRef.current.add(event.deliveryId)
        onStoreScanned?.(event)
      }
      for (const event of newlyCompleted) {
        if (completedRef.current.has(event.deliveryId)) continue
        completedRef.current.add(event.deliveryId)
        onStoreCompleted?.(event)
      }
    }

    async function tick() {
      const trip = getTrip()
      if (!trip || trip.status !== 'in_progress' || !tripNeedsReceivingPoll(trip)) {
        return
      }

      if (shouldUseDelmanMock()) {
        const { trip: next, newlyScanned, newlyCompleted } = applyMockReceivingPoll(trip)
        if (newlyScanned.length === 0 && newlyCompleted.length === 0) return
        onTripUpdate(next)
        emitEvents(newlyScanned, newlyCompleted)
        return
      }

      try {
        const next = await fetchTripDetailFromApi(trip.id)
        const { scanned, completed } = diffReceivingEvents(trip, next)
        if (scanned.length === 0 && completed.length === 0) return
        onTripUpdate(next)
        emitEvents(scanned, completed)
      } catch {
        // tetap pakai data lokal saat API gagal
      }
    }

    tick()
    const timer = window.setInterval(() => void tick(), POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [enabled, getTrip, onTripUpdate, onStoreScanned, onStoreCompleted])
}
